import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiLongRunningRequestTimeoutMs } from "../config";
import useApiHelper from "../hooks/useApiHelper";
import { IInboxConversationRow, IInboxPartContext, IInboxThread } from "../models/messagesInbox/IInboxApi";
import { IInboxConversation } from "../models/messagesInbox/IInboxConversation";
import { DefaultResponseWithData } from "../models/system";
import { buildSampleConversations } from "../stubs/messagesInboxSamples";
import apiConfig from "../utils/apiConfig";
import { conversationFromRow } from "../utils/inboxApiMapping";

/** How far back the list looks on eBay (the API's window). */
export const INBOX_DAYS_TO_SEARCH = 14;

export const messagesInboxKeys = {
  all: ["messagesInbox"] as const,
  conversations: (live: boolean) => ["messagesInbox", live ? "conversations" : "sample"] as const,
  thread: (conversationId?: string) => ["messagesInbox", "thread", conversationId] as const,
  partContext: (partId?: string, buyer?: string, orderNumber?: string) => ["messagesInbox", "partContext", partId, buyer, orderNumber] as const,
  /**
   * Prefix of every partContext key for the part, whoever the buyer is. Invalidate with this: React
   * Query only matches a prefix, and partContext(partId) with its trailing undefineds matches nothing.
   */
  partContextAll: (partId: string) => ["messagesInbox", "partContext", partId] as const,
};

/** Conversation ids are opaque ("ebay:123:abc", "partpro:9"), so they're always encoded in a URL. */
export const conversationPath = (conversationId: string) => `/messagesinbox/conversations/${encodeURIComponent(conversationId)}`;

async function unwrap<T>(request: Promise<DefaultResponseWithData<T>>): Promise<T> {
  const res = await request;
  if (!res.success) throw new Error(res.message);
  return res.data;
}

/**
 * The inbox list. Live (the "Messages Inbox" feature flag): the API, which reads eBay and PartPro
 * on every call – slow (10–30 s), so it isn't refetched on focus. Otherwise (localhost only) the
 * sample conversations, with no API calls at all.
 */
export default function useMessagesInbox(live: boolean, componentName = "useMessagesInbox") {
  // Pages eBay and PartPro per call, so a busy account can run past the default JSON timeout.
  const { get } = useApiHelper({ ...apiConfig(componentName), timeoutMs: apiLongRunningRequestTimeoutMs });
  const queryClient = useQueryClient();

  const conversations = useQuery<IInboxConversation[]>(
    messagesInboxKeys.conversations(live),
    async () => {
      if (!live) return buildSampleConversations();
      const rows = await unwrap(get<DefaultResponseWithData<IInboxConversationRow[]>>(`/messagesinbox/conversations?daysToSearch=${INBOX_DAYS_TO_SEARCH}`));
      return rows.map(conversationFromRow);
    },
    live ? { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 } : { staleTime: Infinity, refetchOnWindowFocus: false }
  );

  /** Reloads the list, the open thread and the part context from eBay and PartPro. */
  const refresh = () => queryClient.invalidateQueries(messagesInboxKeys.all);

  return { conversations, refresh };
}

/** The open conversation's messages, oldest first (live only). */
export function useMessagesInboxThread(conversationId: string | undefined, enabled: boolean, componentName = "useMessagesInboxThread") {
  const { get } = useApiHelper(apiConfig(componentName));

  const thread = useQuery<IInboxThread>(
    messagesInboxKeys.thread(conversationId),
    () => unwrap(get<DefaultResponseWithData<IInboxThread>>(`${conversationPath(conversationId as string)}/messages`)),
    { enabled: enabled && !!conversationId, staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 }
  );

  return { thread };
}

/**
 * Part, donor and order for the side panel (live only). The part is shared by every conversation
 * about its listing, but the order belongs to one buyer. The conversation's buyer (and, for PartPro,
 * its order number) go with the request so the API only returns the order to the conversation it
 * belongs to.
 */
export function useMessagesInboxPartContext(
  partId: string | undefined,
  enabled: boolean,
  owner: { buyer?: string; orderNumber?: string } = {},
  componentName = "useMessagesInboxPartContext"
) {
  const { get } = useApiHelper(apiConfig(componentName));
  const query = new URLSearchParams();
  if (owner.buyer) query.set("buyer", owner.buyer);
  if (owner.orderNumber) query.set("orderNumber", owner.orderNumber);
  const queryString = query.toString() ? `?${query.toString()}` : "";

  const partContext = useQuery<IInboxPartContext>(
    messagesInboxKeys.partContext(partId, owner.buyer, owner.orderNumber),
    () => unwrap(get<DefaultResponseWithData<IInboxPartContext>>(`/messagesinbox/parts/${encodeURIComponent(partId as string)}/context${queryString}`)),
    { enabled: enabled && !!partId, staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 }
  );

  return { partContext };
}
